import { useEffect, useState } from "react";

/**
 * WHAT THE LIST KNOWS ABOUT EDITING THAT NO SINGLE ROW CAN: which cell holds the editor,
 * and which cells are still being SAVED.
 *
 * Both facts span the row/cell boundary, and neither can live in a row. The second one is
 * the newer of the two and is the reason this file grew: the editor closes the instant a
 * commit fires, WITHOUT waiting for the round trip, so by the time the write is in flight
 * the component that issued it is gone and the cell that has to show "saving" was mounted
 * by a different render of a different component. There is nothing between them except
 * this module.
 *
 * ── Why a module-level registry and not a context ─────────────────────────────────
 *
 * A provider is the React-shaped answer and it would have to be mounted in
 * `CrmWorkspace`, which this change does not own. Beyond ownership it would also
 * re-render every row on every open, to communicate one bit to at most two of them — and
 * for the saving state it would re-render every row on every keystroke-driven save, which
 * is precisely the "one pending save must not freeze the table" property being protected.
 *
 * The registry is a single mutable slot plus a small keyed map, which is exactly what the
 * invariants are: there is one list on screen (the page renders one `CrmWorkspace`), so
 * one module-level "who has the editor" is a faithful model of it rather than a shortcut,
 * and per-cell save state is genuinely per-cell.
 *
 * ── Surviving a page change is a FEATURE here, not a leak ────────────────────────
 *
 * Module state outlives the list. That is the right lifetime for both halves: a write is
 * not cancelled when its component goes away (see `CrmCellEditor`), so a realtor who
 * navigates away mid-save and comes straight back finds the cell still marked saving
 * rather than looking idle while a request is in flight. What that lifetime demands in
 * return is that every save path clears itself — `endCrmCellSave` runs on BOTH the success
 * and the failure branch — or a cell would spin forever with nothing left to stop it.
 */

// ───────────────────────────────────────────────────────────────────────────────
// 1. The editor slot — one open editor in the whole list
// ───────────────────────────────────────────────────────────────────────────────

/**
 * ── The release contract ─────────────────────────────────────────────────────────
 *
 * `claim` is called with a STABLE per-row callback (a `useCallback` with no deps), so a
 * row opening a second of its own cells does not close itself: the registry compares by
 * identity and a re-claim by the same holder is a no-op. `release` must be called from
 * the row's unmount cleanup as well as on close — a row scrolled out of a virtualised
 * list, or removed by a refetch, would otherwise leave a dangling closure that the next
 * claim invokes against an unmounted component.
 */
let currentRelease: (() => void) | null = null;

/** Take the list's single editor slot, closing whoever held it. */
export function claimCrmCellEditor(release: () => void): void {
  if (currentRelease && currentRelease !== release) currentRelease();
  currentRelease = release;
}

/** Give the slot back. Safe to call when someone else already took it. */
export function releaseCrmCellEditor(release: () => void): void {
  if (currentRelease === release) currentRelease = null;
}

// ───────────────────────────────────────────────────────────────────────────────
// 2. Cell identity, and why the CELL is the one that names it
// ───────────────────────────────────────────────────────────────────────────────

/**
 * A cell is `(contact, column)`. `\u0000` joins the two because a contact id is an E.164
 * phone and a column label is Hebrew prose — neither can contain a NUL, so the pair can
 * never be ambiguous the way a `-` or a `:` join could.
 *
 * ⚠️ THE COLUMN HALF IS THE COLUMN'S HEADER TEXT, NOT ITS ID OR ITS DIMENSION, and that
 * is forced by what each side actually holds. `CrmEditableCell` is handed `label`
 * (`column.header`) and `contact` and nothing else — the row passes it no column id and no
 * dimension, and the row is not ours to change. The editor, for its part, is handed a
 * `dimension` and never sees a label.
 *
 * So the two halves cannot independently derive the same key, and the design does not ask
 * them to: the CELL computes the key and ANNOUNCES it on the click that opens the editor,
 * and the editor simply consumes whatever it was handed (see `takeCrmCellEditTarget`).
 * The key is opaque to the editor, so there is no mapping table to keep in sync and no way
 * for the two sides to disagree about what a cell is called.
 *
 * Headers are unique per column by construction — two columns sharing one would be
 * indistinguishable to the reader long before they collided here — and the one column
 * whose cell serves two dimensions (money: `budget` or `budgetRent`, chosen per contact)
 * is correctly ONE cell with ONE key.
 */
export type CrmCellKey = string;

export const crmCellKey = (contactId: string, columnLabel: string): CrmCellKey =>
  `${contactId}\u0000${columnLabel}`;

/** What a cell tells the editor about itself. The label rides along for the failure toast. */
export type CrmCellEditTarget = { key: CrmCellKey; label: string };

/**
 * The announcement is CONSUMED on read, and that is the safety property.
 *
 * Not every editor is opened by a `CrmEditableCell`: the identity column's note trigger
 * lives in `CrmIdentityCell`, which is not part of this mechanism and announces
 * nothing. If the announcement merely persisted, the editor opened from one of those would
 * pick up whichever cell was edited LAST and hang its spinner — and, on failure, its
 * revert — on a completely unrelated column. Consuming it means that editor reads `null`
 * and simply does without a cell marker, which is a missing indicator rather than a wrong
 * one.
 *
 * The age check covers the only way an announcement can be orphaned: a cell announcing and
 * then never producing an editor (the row unmounted inside the same gesture). An
 * announcement is consumed microseconds after it is made — same click, same commit — so
 * anything older than a tick belongs to nobody.
 */
const ANNOUNCEMENT_TTL_MS = 1_000;

let announced: (CrmCellEditTarget & { at: number }) | null = null;

/** Called by the cell on the click that opens the editor, BEFORE the editor mounts. */
export function announceCrmCellEdit(target: CrmCellEditTarget): void {
  announced = { ...target, at: Date.now() };
}

/** Called by the editor once, at mount. Returns null when nobody announced. */
export function takeCrmCellEditTarget(): CrmCellEditTarget | null {
  const claim = announced;
  announced = null;
  if (!claim) return null;
  if (Date.now() - claim.at > ANNOUNCEMENT_TTL_MS) return null;
  return { key: claim.key, label: claim.label };
}

// ───────────────────────────────────────────────────────────────────────────────
// 3. Which cells are saving
// ───────────────────────────────────────────────────────────────────────────────

/**
 * `latest` is the attempt number of the most recent save STARTED for this cell; `inFlight`
 * is how many are still outstanding. They are separate because they answer different
 * questions and settle at different times: `inFlight` drives the spinner and drops back to
 * zero the moment a request lands, while `latest` outlives it and is what tells a late
 * callback — a revert, a retry button pressed ten seconds afterwards — that the cell has
 * moved on without it.
 */
type CellSaves = { latest: number; inFlight: number };

const saves = new Map<CrmCellKey, CellSaves>();
const watchers = new Map<CrmCellKey, Set<() => void>>();

/**
 * One counter for the whole list rather than one per cell, so an attempt number is
 * globally unique and can never be confused with another cell's.
 */
let attempts = 0;

const entryFor = (key: CrmCellKey): CellSaves => {
  const existing = saves.get(key);
  if (existing) return existing;
  const created: CellSaves = { latest: 0, inFlight: 0 };
  saves.set(key, created);
  return created;
};

const notifyWatchers = (key: CrmCellKey): void => {
  watchers.get(key)?.forEach((notify) => notify());
};

/**
 * Mark a save as started. Returns the attempt number the caller must quote back.
 *
 * Entries are never pruned, deliberately. `isCrmCellSaveCurrent` has to be answerable
 * AFTER the save it is asked about has settled — that is the whole point of the retry
 * guard — and a pruned entry reads as `latest: 0`, which would report every retry as
 * superseded and silently refuse it. The cost is two numbers per (contact, column) the
 * realtor has actually edited in this session, cleared by any reload; that is not a leak
 * worth a sweeper.
 */
export function beginCrmCellSave(key: CrmCellKey): number {
  const entry = entryFor(key);
  attempts += 1;
  entry.latest = attempts;
  entry.inFlight += 1;
  if (entry.inFlight === 1) notifyWatchers(key);
  return attempts;
}

/** Mark a save as settled — success or failure, both must call it. */
export function endCrmCellSave(key: CrmCellKey): void {
  const entry = saves.get(key);
  if (!entry || entry.inFlight === 0) return;
  entry.inFlight -= 1;
  if (entry.inFlight === 0) notifyWatchers(key);
}

/**
 * Is this attempt still the last word on this cell?
 *
 * The question a late callback has to ask before it touches the row. A revert from an
 * older attempt would otherwise undo a NEWER value the realtor has since saved
 * successfully, which is worse than the failure it is reacting to: the write that
 * succeeded would visibly disappear.
 */
export function isCrmCellSaveCurrent(key: CrmCellKey, attempt: number): boolean {
  return (saves.get(key)?.latest ?? 0) === attempt;
}

export function isCrmCellSaving(key: CrmCellKey): boolean {
  return (saves.get(key)?.inFlight ?? 0) > 0;
}

/**
 * Subscribe one cell to its own save state.
 *
 * `useState` + `useEffect` rather than `useSyncExternalStore`, which does not exist on
 * React 17 and whose shim is not a dependency here. The initial value is read in the state
 * INITIALISER, not in the effect, because the common case is exactly the racy one: a
 * commit starts the save and closes the editor in the same batch, so this cell mounts with
 * the save already in flight and an effect-only read would paint one frame of an idle
 * cell. The effect re-reads on subscribe anyway, which closes the remaining window between
 * render and subscription.
 */
export function useCrmCellSaving(key: CrmCellKey): boolean {
  const [saving, setSaving] = useState(() => isCrmCellSaving(key));

  useEffect(() => {
    const read = () => setSaving(isCrmCellSaving(key));
    read();

    const set = watchers.get(key) ?? new Set<() => void>();
    watchers.set(key, set);
    set.add(read);

    return () => {
      set.delete(read);
      if (set.size === 0) watchers.delete(key);
    };
  }, [key]);

  return saving;
}
